import { useNamespacedNavigate, useOperations, useUxFeatures } from '@frontend/hooks';

import { OperationsName } from '../types/user';

import AccessDenied from './AccessDenied';

function getAllowedRoute(allowedPermissions: OperationsName[], reorgRagContext: boolean) {
  // Check new permission first, then old for backward compat
  if (allowedPermissions.includes('use_chat')) {
    return '/chat';
  }
  if (
    allowedPermissions.includes('view_instructions') ||
    allowedPermissions.includes('manage_instructions')
  ) {
    return reorgRagContext ? '/context' : '/instructions';
  }
  if (allowedPermissions.includes('view_agents')) {
    return '/agents';
  }
  return null;
}

/**
 * Component to check if user has at least one of the required operations to access the children components.
 * @param operations - list of operations of which at least one is required to access the children (checked using Logical OR)
 * @param children - children components to render if user has access
 * @returns JSX.Element - children components if user has access, otherwise redirects to allowed route or shows Access Denied page
 */
function CanUser({
  operations,
  children,
}: {
  operations: OperationsName[];
  children: JSX.Element;
}) {
  const { operations: userOperations, canUser } = useOperations();
  const navigate = useNamespacedNavigate();
  const { reorgRagContext } = useUxFeatures();
  if (!canUser(operations)) {
    const allowedRoute = getAllowedRoute(
      userOperations?.filter(p => p.allowed).map(p => p.name) ?? [],
      reorgRagContext
    );
    if (allowedRoute) {
      navigate(allowedRoute);
      return null;
    }
    return <AccessDenied />;
  }

  return children;
}

export default CanUser;
